// collapse-sgf-loss-v1 untrusted-import parser (M6-D4).
//
// The imported SGF is NEVER a state carrier: it yields only a list of
// visible-mainline intents ({color, canvasPoint|null} with null = PASS)
// that the client replays as NORMAL/PASS commands against a fresh session.
// The C++ reducer validates every intent; the client patches nothing.
//
// Fail-closed rules (docs/设计文档/05-协议与Schema.md §8, SGF-01):
//   - FF[4]/GM[1]/SZ[19] required (the wire canvas is 19x19 only);
//   - CLP, when present, must be exactly "collapse-sgf-loss-v1" — an
//     unrecognized Collapse extension is rejected, never reinterpreted;
//   - setup properties (AB/AW/AE) are accepted ONLY under the loss profile
//     (our own exports close with the FINAL_VISIBLE_BOARD artifact) and are
//     never turned into intents; under any other profile they are rejected;
//   - variation branches are rejected (only the mainline is imported);
//   - every move replays as NORMAL: the kind is LOST by the profile, and the
//     UI must declare this loss in front of the user (never silently).
// Resource profile (fail-closed, checked before use): text <= 1 MiB (the
// exporter's own cap), <= 4096 nodes, <= 4096 moves.

import { CANVAS_SIZE, PASS_ACTION_ID } from "./codec.js";

export const SGF_LOSS_PROFILE = "collapse-sgf-loss-v1";
export const SGF_MAX_TEXT_BYTES = 1024 * 1024;
export const SGF_MAX_NODES = 4096;
export const SGF_MAX_MOVES = 4096;

export interface SgfMove {
  color: "BLACK" | "WHITE";
  // Canvas point 0..360, or null for PASS.
  point: number | null;
}

export interface SgfImport {
  moves: SgfMove[];
  // RE[...] text when present (display only, never authoritative).
  resultText: string | null;
  // True when the file carries CLP[collapse-sgf-loss-v1].
  lossProfileDeclared: boolean;
}

export class SgfError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "SgfError";
  }
}

function fail(message: string): never {
  throw new SgfError(message);
}

// Unescape an SGF text value (backslash escapes the next character).
function unescapeText(raw: string): string {
  let out = "";
  for (let i = 0; i < raw.length; i++) {
    const ch = raw[i]!;
    if (ch === "\\" && i + 1 < raw.length) out += raw[++i]!;
    else out += ch;
  }
  return out;
}

function sgfPoint(value: string, color: string): number | null {
  // Empty value is a PASS; "tt" is the conventional PASS on boards <= 19.
  if (value === "" || value === "tt") return null;
  if (value.length !== 2) fail(`${color}[${value}]: coordinate must be two letters`);
  const x = value.charCodeAt(0) - 97; // 'a'
  const y = value.charCodeAt(1) - 97;
  if (x < 0 || x >= CANVAS_SIZE || y < 0 || y >= CANVAS_SIZE)
    fail(`${color}[${value}]: point outside the 19x19 canvas`);
  return y * CANVAS_SIZE + x;
}

interface RawNode {
  props: { ident: string; values: string[] }[];
}

const utf8Encoder = new TextEncoder();

function tokenizeNodes(text: string): RawNode[] {
  // The cap is in UTF-8 bytes, mirroring the gateway's Buffer.byteLength.
  if (utf8Encoder.encode(text).length > SGF_MAX_TEXT_BYTES)
    fail("SGF text exceeds the 1 MiB cap");
  const nodes: RawNode[] = [];
  let i = 0;
  const skipWs = () => {
    while (i < text.length && /\s/.test(text[i]!)) i++;
  };
  skipWs();
  if (text[i] !== "(") fail("SGF must open with a game tree");
  i++;
  // Exactly one branch: any further '(' after the first node sequence is a
  // variation and fails closed.
  let sawNode = false;
  for (;;) {
    skipWs();
    if (i >= text.length) fail("SGF game tree is not closed");
    const ch = text[i]!;
    if (ch === ")") {
      i++;
      break;
    }
    if (ch === "(") fail("SGF variation branches are not importable (mainline only)");
    if (ch !== ";") fail(`SGF: unexpected '${ch}' in the game tree`);
    i++;
    sawNode = true;
    const node: RawNode = { props: [] };
    nodes.push(node);
    if (nodes.length > SGF_MAX_NODES) fail("SGF exceeds the 4096-node cap");
    for (;;) {
      skipWs();
      if (i >= text.length || !/[A-Za-z]/.test(text[i]!)) break;
      let ident = "";
      while (i < text.length && /[A-Za-z]/.test(text[i]!)) {
        ident += text[i]!;
        i++;
      }
      // PropIdent canonically uppercases; reject lowercase-only drift.
      if (ident !== ident.toUpperCase()) fail(`SGF property '${ident}' is not canonical uppercase`);
      const values: string[] = [];
      for (;;) {
        skipWs();
        if (text[i] !== "[") break;
        i++;
        let raw = "";
        for (;;) {
          if (i >= text.length) fail(`SGF property '${ident}' value is not closed`);
          const c = text[i]!;
          if (c === "\\" && i + 1 < text.length) {
            raw += c + text[i + 1]!;
            i += 2;
            continue;
          }
          if (c === "]") {
            i++;
            break;
          }
          raw += c;
          i++;
        }
        values.push(raw);
      }
      if (values.length === 0) fail(`SGF property '${ident}' carries no value`);
      node.props.push({ ident, values });
    }
  }
  skipWs();
  if (i !== text.length) fail("SGF: trailing content after the game tree");
  if (!sawNode) fail("SGF carries no nodes");
  return nodes;
}

export function parseSgfImport(text: string): SgfImport {
  if (typeof text !== "string" || text.length === 0) fail("empty SGF");
  const nodes = tokenizeNodes(text);
  const root = nodes[0]!;
  const rootProp = (ident: string): string[] | null => {
    const hits = root.props.filter((p) => p.ident === ident);
    if (hits.length > 1) fail(`SGF root carries duplicate ${ident}`);
    return hits.length === 1 ? hits[0]!.values : null;
  };
  const single = (ident: string, values: string[]): string => {
    if (values.length !== 1) fail(`SGF property '${ident}' must carry exactly one value`);
    return unescapeText(values[0]!);
  };
  const ff = rootProp("FF");
  if (ff === null || single("FF", ff) !== "4") fail("SGF must declare FF[4]");
  const gm = rootProp("GM");
  if (gm === null || single("GM", gm) !== "1") fail("SGF must declare GM[1] (go)");
  const sz = rootProp("SZ");
  if (sz === null || single("SZ", sz) !== "19")
    fail("SGF must declare SZ[19] (the wire canvas is 19x19 only)");

  // CLP anywhere in the tree is a Collapse profile claim: every occurrence
  // and every value must name the loss profile exactly — an unrecognized
  // extension is rejected, never reinterpreted as NORMAL.
  let lossProfileDeclared = false;
  for (const node of nodes) {
    for (const prop of node.props) {
      if (prop.ident !== "CLP") continue;
      const profile = single("CLP", prop.values);
      if (profile !== SGF_LOSS_PROFILE)
        fail(`unrecognized Collapse SGF profile '${profile}' (rejecting, not reinterpreting)`);
      lossProfileDeclared = true;
    }
  }
  const resultText = (() => {
    const re = rootProp("RE");
    return re === null ? null : single("RE", re);
  })();

  const moves: SgfMove[] = [];
  const setupPoints = { AB: new Set<number>(), AW: new Set<number>(), AE: new Set<number>() };
  let sawSetup = false;
  for (const node of nodes) {
    let nodeMoves = 0;
    for (const prop of node.props) {
      if (prop.ident === "B" || prop.ident === "W") {
        if (prop.values.length !== 1) fail(`SGF move ${prop.ident} must carry exactly one value`);
        const color = prop.ident === "B" ? "BLACK" : "WHITE";
        moves.push({ color, point: sgfPoint(prop.values[0]!, prop.ident) });
        if (moves.length > SGF_MAX_MOVES) fail("SGF exceeds the 4096-move cap");
        nodeMoves++;
        continue;
      }
      if (prop.ident === "AB" || prop.ident === "AW" || prop.ident === "AE") {
        // The loss profile's FINAL_VISIBLE_BOARD artifact: display material,
        // never intents. Outside the declared profile it is not importable.
        if (!lossProfileDeclared)
          fail("SGF setup stones (AB/AW/AE) require the collapse-sgf-loss-v1 profile declaration");
        // Coordinates are validated even though they never become intents.
        sawSetup = true;
        const bucket = setupPoints[prop.ident];
        for (const value of prop.values) {
          const point = sgfPoint(value, prop.ident);
          if (point === null)
            fail(`setup property ${prop.ident} carries a non-point value`);
          if (bucket.has(point)) fail(`setup property ${prop.ident} repeats point ${point}`);
          bucket.add(point);
        }
        continue;
      }
      // All other properties (KM, RE, comments, markup) are non-authoritative
      // display material and are ignored by construction.
    }
    if (nodeMoves > 1) fail("SGF node carries more than one move");
  }
  if (sawSetup) {
    // FINAL_VISIBLE_BOARD contract (collapseserver.cpp): AE/AB/AW partition
    // the board exactly once — disjoint and covering all 361 points.
    const covered = new Set<number>();
    for (const bucket of [setupPoints.AB, setupPoints.AW, setupPoints.AE]) {
      for (const point of bucket) {
        if (covered.has(point))
          fail("the final-board setup properties overlap");
        covered.add(point);
      }
    }
    if (covered.size !== CANVAS_SIZE * CANVAS_SIZE)
      fail("the final-board setup properties do not partition the 361 points");
  }
  return { moves, resultText, lossProfileDeclared };
}

// The actionId an imported move replays as: NORMAL at the canvas point, or
// PASS. Kind information is lost by the profile; the import UI must declare
// this to the user before replay.
export function importActionId(move: SgfMove): number {
  return move.point === null ? PASS_ACTION_ID : move.point;
}

// --- cross-reload stash (main.tsx sessionStorage handoff) -------------------

// Versioned marker making the confirmation contract explicit: a stash entry
// is produced only by the import UI after the loss declaration was shown.
export const PENDING_IMPORT_MARKER = "collapse-sgf-import-v1";

export interface StashedImport {
  sessionId: string;
  moves: SgfMove[];
}

// Defensive revalidation of the untrusted sessionStorage record across the
// reload boundary. Returns null on ANY deviation (the record is dropped).
export function parseStashedImport(raw: string): StashedImport | null {
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) return null;
    const record = parsed as { marker?: unknown; sessionId?: unknown; moves?: unknown };
    if (record.marker !== PENDING_IMPORT_MARKER) return null;
    if (typeof record.sessionId !== "string" || record.sessionId.length > 128) return null;
    if (!Array.isArray(record.moves) || record.moves.length > SGF_MAX_MOVES) return null;
    const moves: SgfMove[] = record.moves.map((entry) => {
      const e = entry as { color?: unknown; point?: unknown };
      if (e.color !== "BLACK" && e.color !== "WHITE") throw new Error("bad color");
      if (
        e.point !== null &&
        !(typeof e.point === "number" && Number.isSafeInteger(e.point) && e.point >= 0 && e.point <= 360)
      )
        throw new Error("bad point");
      return { color: e.color, point: e.point as number | null };
    });
    return { sessionId: record.sessionId, moves };
  } catch {
    return null;
  }
}
